import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import { XGridOverflowCell } from '@AMIEWEB/Common/XGrid/cells/XGridOverflowCell';
import { GridCellCheckboxEdit, GridCellCheckboxView } from './components/gridComponents/GridCellCheckbox';
import { renderDefaultGridCell } from './components/gridComponents/DefaultGridCell';
import { GridCellEditFacility } from './components/gridComponents/GridCellEditFacility';
import { GridCellYesNo } from './components/gridComponents/GridCellYesNo';
import { renderGridCellDate } from './components/gridComponents/GridCellDate';
import { renderDropdownCell } from './components/gridComponents/GridDropdownCell';
import countriesData from 'app/assets/jsons/Country.json';
import statesData from 'app/assets/jsons/State.json';
import { renderEditTextareaCell } from './components/gridComponents/GridEditTextareaCell';
import { renderGridTextInputCell } from './components/gridComponents/GridTextInputCell';
import { renderTypeAheadCell } from './components/gridComponents/GridTypeAheadCell';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
enum CountryNames {
  USA = 'USA',
  CANADA = 'Canada',
}
enum ExcludedCountries {
  VIRGIN_ISLANDS = 213,
  PUERTO_RICO = 17,
}

const NUMBER_OF_BEDS_INPUT_PROPS = { min: 0, max: 9999 };

export const defaultWorkExperienceColumn = {
  ...XGridDefaultColumn,
  flex: 0,
};

export const hasEditGridCellAccess = (user: any) => {
  if (
    Authorized(
      [
        userRoles.recruitment_TeamMember,
        userRoles.recruitment_Leadership,
        userRoles.clinical_Director,
        userRoles.clinical_Manager,
        userRoles.clinical_QualificationsSpecialist,
        userRoles.clinical_ReviewAnalyst,
        userRoles.accountManagement_Leadership,
        userRoles.accountManagement_TeamMember,
      ],
      user.userInfo,
    )
  ) {
    return true;
  } else {
    return false;
  }
};

export const hasDeleteGridCellAccess = (user: any) => {
  if (
    Authorized(
      [
        userRoles.recruitment_TeamMember,
        userRoles.recruitment_Leadership,
        userRoles.clinical_Director,
        userRoles.clinical_Manager,
        userRoles.clinical_QualificationsSpecialist,
        userRoles.clinical_ReviewAnalyst,
      ],
      user.userInfo,
    )
  ) {
    return true;
  } else {
    return false;
  }
};
export const getWorkExperienceGridColumns = (
  t: Function,
  isEditable: boolean,
  classes: any,
  disciplines: any[] = [],
  specialtiesLookup: any[] = [],
  travelCompanies: any[] = [],
  reasonForLeavingOptions: any[] = [],
) => {
  const columns = [
    {
      ...defaultWorkExperienceColumn,
      field: 'facilityName',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.facilityName'),
      width: 260,
      renderCell: GridCellEditFacility,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'approvedReferenceCount',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.approved'),
      width: 70,
      align: 'center',
      renderCell: renderDefaultGridCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'inProgressReferenceCount',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.inProgress'),
      width: 90,
      align: 'center',
      renderCell: renderDefaultGridCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'unapprovedReferenceCount',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.unapproved'),
      width: 90,
      align: 'center',
      renderCell: renderDefaultGridCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'verifiedReferenceCount',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.verified'),
      width: 70,
      align: 'center',
      renderCell: renderDefaultGridCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'hasEMPVReference',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.empv'),
      width: 65,
      align: 'center',
      renderCell: params => GridCellYesNo(params),
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'placementID',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.pID'),
      width: 90,
      renderCell: renderDefaultGridCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'country',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.country'),
      width: 150,
      type: 'singleSelect',
      editable: isEditable,
      renderCell: renderDefaultGridCell,
      valueOptions: countriesData?.countries
        ?.filter(x => x.ID !== ExcludedCountries.PUERTO_RICO && x.ID !== ExcludedCountries.VIRGIN_ISLANDS)
        .map(e => ({
          id: e.ID,
          value: e.Description,
          label: e.Description,
        })),
      renderEditCell: renderTypeAheadCell,
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'city',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.city'),
      width: 150,
      editable: isEditable,
      type: 'singleSelect',
      renderCell: params =>
        renderDefaultGridCell({ ...params, hasError: !isValidCity(params?.row), tooltipText: 'Required' }),
      renderEditCell: renderTypeAheadCell,
      cellClassName: ({ row }) => (!isValidCity(row) ? classes.isErrorCell : null),
    },
    {
      ...defaultWorkExperienceColumn,
      field: 'state',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.state'),
      width: 150,
      editable: isEditable,
      type: 'singleSelect',
      renderCell: params =>
        renderDefaultGridCell({ ...params, hasError: !isValidState(params?.row), tooltipText: 'Required' }),
      valueOptions: params => {
        const country = params?.row?.country;
        const countryId: number = country === CountryNames.USA ? 1 : country === CountryNames.CANADA ? 2 : 0;
        if (country === CountryNames.USA || country === CountryNames.CANADA) {
          const statesList = statesData.states
            .filter(e => e.CountryID === countryId)
            .map(e => ({ id: e.ID, value: e.Description, label: e.Description }));
          return statesList;
        } else {
          return [];
        }
      },
      renderEditCell: renderTypeAheadCell,
      cellClassName: ({ row }) => (!isValidState(row) ? classes.isErrorCell : null),
    },
    {
      field: 'zip',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.zip'),
      width: 120,
      editable: isEditable,
      renderCell: renderDefaultGridCell,
      renderEditCell: params => renderGridTextInputCell({ ...params, inputProps: { maxLength: 5 } }),
    },
    {
      field: 'disciplineAbbr',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.discipline'),
      width: 135,
      editable: isEditable,
      type: 'singleSelect',
      renderCell: params =>
        !params?.row?.disciplineAbbr
          ? renderDefaultGridCell({
              ...params,
              hasError: !params?.row?.disciplineAbbr,
              tooltipText: 'Required',
            })
          : XGridOverflowCell()()(params),
      renderEditCell: renderTypeAheadCell,
      cellClassName: ({ row }) => (!row?.isTimeOff && !row?.disciplineAbbr ? classes.isErrorCell : null),
    },
    {
      field: 'specialtyAbbr',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.specialty'),
      width: 135,
      editable: isEditable,
      type: 'singleSelect',
      renderCell: params =>
        !params?.row?.specialtyAbbr
          ? renderDefaultGridCell({
              ...params,
              hasError: !params?.row?.specialtyAbbr,
              tooltipText: 'Required',
            })
          : XGridOverflowCell()()(params),
      renderEditCell: renderTypeAheadCell,
      cellClassName: ({ row }) => (!row?.isTimeOff && !row?.specialtyAbbr ? classes.isErrorCell : null),
    },
    {
      field: 'workHistoryType',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.workHistoryType'),
      width: 170,
      editable: false,
      renderCell: renderDefaultGridCell,
    },
    {
      field: 'startedOn',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.startDate'),
      width: 135,
      editable: isEditable,
      type: 'date',
      renderCell: params =>
        renderDefaultGridCell({
          ...params,
          hasError: !isValidStartDate(params?.row),
          tooltipText: !params?.row?.startedOn ? 'Required' : 'Invalid',
        }),
      renderEditCell: renderGridCellDate,
      cellClassName: ({ row }) => (!isValidStartDate(row) ? classes.isErrorCell : null),
    },
    {
      field: 'endedOn',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.endDate'),
      width: 135,
      editable: isEditable,
      type: 'date',
      renderCell: params =>
        renderDefaultGridCell({ ...params, hasError: !isValidEndDate(params?.row), tooltipText: 'Invalid' }),
      renderEditCell: renderGridCellDate,
      cellClassName: ({ row }) => (!isValidEndDate(row) ? classes.isErrorCell : null),
    },
    {
      field: 'currentlyEmployed',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.currentlyWorking'),
      width: 130,
      align: 'center',
      editable: isEditable,
      type: 'boolean',
      renderCell: GridCellCheckboxView,
      renderEditCell: GridCellCheckboxEdit,
    },
    {
      field: 'equipmentProcedures',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.equipmentProcedures'),
      width: 150,
      editable: isEditable,
      renderCell: params =>
        !isValidEquipmentAndProcedure(params?.row)
          ? renderDefaultGridCell({
              ...params,
              hasError: !isValidEquipmentAndProcedure(params?.row),
              tooltipText: 'Maximum Characters Reached',
            })
          : XGridOverflowCell()()(params),
      renderEditCell: renderEditTextareaCell,
      cellClassName: ({ row }) => (!isValidEquipmentAndProcedure(row) ? classes.isErrorCell : null),
    },
    {
      field: 'reasonForLeaving',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.reasonForLeaving'),
      width: 180,
      editable: isEditable,
      type: 'singleSelect',
      valueOptions: reasonForLeavingOptions,
      renderCell: params => XGridOverflowCell()()(params),
      renderEditCell: renderDropdownCell,
    },
    {
      field: 'position',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.positionHeld'),
      width: 150,
      editable: isEditable,
      type: 'string',
      renderCell: XGridOverflowCell()(),
      renderEditCell: params => renderGridTextInputCell({ ...params, inputProps: { maxLength: 50 } }),
    },
    {
      field: 'unit',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.unit'),
      width: 150,
      type: 'string',
      editable: isEditable,
      renderCell: XGridOverflowCell()(),
      renderEditCell: params => renderGridTextInputCell({ ...params, inputProps: { maxLength: 50 } }),
    },
    {
      field: 'numberOfBedsInFacility',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.numberOfBeds'),
      width: 150,
      align: 'center',
      editable: isEditable,
      type: 'number',
      renderCell: params =>
        renderDefaultGridCell({ ...params, hasError: !isValidNumberOfBeds(params?.row), tooltipText: 'Invalid' }),
      renderEditCell: params => renderGridTextInputCell({ ...params, inputProps: NUMBER_OF_BEDS_INPUT_PROPS }),
      cellClassName: ({ row }) => (!isValidNumberOfBeds(row) ? classes.isErrorCell : null),
    },
    {
      field: 'travelCompany',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.staffingAgency'),
      width: 150,
      editable: isEditable,
      type: 'singleSelect',
      valueOptions: travelCompanies?.map(item => ({ value: item?.name, label: item?.name })) || [],
      renderCell: XGridOverflowCell()(),
      renderEditCell: renderTypeAheadCell,
    },
    {
      field: 'isSupervisor',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.supervisor'),
      width: 120,
      align: 'center',
      editable: isEditable,
      renderCell: GridCellCheckboxView,
      renderEditCell: GridCellCheckboxEdit,
    },
    {
      field: 'isTeletherapy',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.teleTherapy'),
      width: 120,
      align: 'center',
      editable: isEditable,
      renderCell: GridCellCheckboxView,
      renderEditCell: GridCellCheckboxEdit,
    },
    {
      field: 'isTeachingHospital',
      headerName: t('candidate.workExperience.workExperienceGridHeaders.teachingHospital'),
      width: 120,
      align: 'center',
      editable: isEditable,
      renderCell: GridCellCheckboxView,
      renderEditCell: GridCellCheckboxEdit,
    },
  ];
  return columns;
};

const isValidValue = (value: any) => {
  if (value === null || value === undefined || value === '' || value === '--') {
    return false;
  }
  return true;
};

const isValidCity = (row: any) => {
  if (row?.isTimeOff) {
    if (row?.country === CountryNames.USA && isValidValue(row?.state) && !isValidValue(row?.city)) {
      return false;
    }
    return true;
  }
  if (!isValidValue(row?.city) && row?.country === CountryNames.USA) {
    return false;
  }
  return true;
};

const isValidState = (row: any) => {
  if (
    !row?.isTimeOff &&
    !isValidValue(row?.state) &&
    (row?.country === CountryNames.USA || row?.country === CountryNames.CANADA)
  ) {
    return false;
  }
  return true;
};

const isValidStartDate = (row: any) => {
  let isValid = true;
  if (row?.startedOn) {
    if (row?.endedOn) {
      isValid = new Date(row?.startedOn) <= new Date(row?.endedOn);
    }
  } else {
    isValid = false;
  }

  return isValid;
};

const isValidEndDate = (row: any) => {
  let isValid = true;
  if (row?.endedOn && row?.startedOn) {
    isValid = new Date(row?.endedOn) >= new Date(row?.startedOn);
  }

  return isValid;
};

const isValidNumberOfBeds = (row: any) => {
  if (
    !row?.isTimeOff &&
    (parseInt(row?.numberOfBedsInFacility) < NUMBER_OF_BEDS_INPUT_PROPS?.min ||
      parseInt(row?.numberOfBedsInFacility) > NUMBER_OF_BEDS_INPUT_PROPS?.max)
  ) {
    return false;
  }
  return true;
};

const isValidEquipmentAndProcedure = (row: any) => {
  if (row?.equipmentProcedures?.length > 1000) {
    return false;
  }
  return true;
};
